import { useClassFactory } from "@pasta/box-v2";
import { Deferred } from "@pasta/deferred";
import { computed, onUnmounted, ref, shallowReactive, watch } from "vue";

export class AsyncOperationManager {
  private deferredMap: Map<string, Deferred>;

  constructor() {
    this.deferredMap = shallowReactive<Map<string, Deferred>>(new Map());
  }

  get loading() {
    return computed(() => {
      return this.deferredMap.size > 0;
    });
  }

  get notifyAsyncOperation() {
    return this.getNotifyFn(this.registerOperation.bind(this));
  }

  getShowSaved(operationName: string) {
    const showSaved = ref(false);
    const stopWatch = watch(
      () => this.loading.value,
      () => {
        if (this.loading.value && this.hasOperation(operationName)) {
          showSaved.value = true;
        } else {
          setTimeout(() => {
            showSaved.value = false;
          }, 1_000);
        }
      }
    );
    onUnmounted(() => {
      stopWatch();
    });
    return showSaved;
  }

  registerOperation(name: string): Deferred {
    const deferred = new Deferred();
    deferred.finally(() => {
      this.deferredMap.delete(name);
    });
    this.deferredMap.set(name, deferred);
    return deferred;
  }

  private hasOperation(name: string) {
    return !!this.deferredMap.get(name);
  }

  private getNotifyFn(registerOperation: (name: string) => Deferred) {
    return async function notifyAsyncOperation(fn: () => Promise<any>, name: string) {
      const deferred = registerOperation(name);
      try {
        await fn();
        deferred.resolve();
      } catch (e) {
        deferred.reject(e);
        throw e;
      }
    };
  }
}

export const [_useAsyncOperationSymbol, useAsyncOperation] = useClassFactory(AsyncOperationManager);

export function useNotifyAsyncOperation() {
  return useAsyncOperation().notifyAsyncOperation;
}
